/**
 * Typed dates and times (DESIGN.md "Date / Time input"): `DD.MM.YYYY` and 24 h `HH:MM`,
 * independent of the browser's locale. Values are ISO (`YYYY-MM-DD`, `HH:MM`).
 * `strict` accepts only complete forms, so half-typed text is never taken as a value.
 */

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "2026-10-25" -> "25.10.2026"; anything else as is. */
export function formatDateText(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : iso;
}

/** "25.10.2026" (also "25/10/2026", "25-10-26", "1.2.2027") -> "2026-10-25", or null. */
export function parseDateText(text: string, strict = false): string | null {
  const match = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4}|\d{2})$/.exec(text.trim());
  if (!match) return null;
  const [, d = "", m = "", y = ""] = match;
  if (strict && y.length !== 4) return null;
  const year = y.length === 2 ? 2000 + Number(y) : Number(y);
  const month = Number(m);
  const day = Number(d);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (year < 1 || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(day)}`;
}

/** "18:30" (also "18.30", "1830", "930", "9") -> "18:30", or null. */
export function parseTimeText(text: string, strict = false): string | null {
  const t = text.trim();
  const match = strict
    ? /^(\d{1,2})[:.](\d{2})$|^(\d{2})(\d{2})$/.exec(t)
    : /^(\d{1,2})(?:[:.]?(\d{2}))?$/.exec(t);
  if (!match) return null;
  const hours = Number(match[1] ?? match[3]);
  const minutes = Number(match[2] ?? match[4] ?? 0);
  if (hours > 23 || minutes > 59) return null;
  return `${pad2(hours)}:${pad2(minutes)}`;
}
